import 'katex/dist/katex.min.css'; // `rehype-katex` does not import the CSS for you

import { styled } from '@mui/system';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import supersub from 'remark-supersub';

import { AugmentedResponseWithSources } from '../../components/DealGPT/SourcesDisplay';
import { selectConversations } from '../../redux/reducers/conversationReducer';
import { Message } from '../../Types/conversation';
import { renderMessage } from '../../util/messageUtils';
import MarkdownTable from '../MarkdownTable';
import CodeCopyButton from './CodeCopyButton';

const StyledSyntaxHighlighter = styled(SyntaxHighlighter)({
  borderRadius: '4px',
});

const CodePre = styled('pre')({
  position: 'relative',
  overflowX: 'auto',
});

const ChatReactMarkdown = ({
  message,
  viewCode = true,
}: {
  message: Partial<Message>;
  viewCode?: boolean;
}) => {
  const { isLoading } = useSelector(selectConversations);

  return (
    <ReactMarkdown
      linkTarget={'_blank'}
      remarkPlugins={[remarkGfm, remarkMath, supersub]}
      rehypePlugins={[rehypeKatex]}
      components={{
        sup: ({ children }) => (
          <AugmentedResponseWithSources
            text={children.toString()}
            sources={message.sources ?? []}
          />
        ),
        pre: ({ className, children }) => (
          <CodePre className={className}>
            <CodeCopyButton loading={isLoading}>{children}</CodeCopyButton>
            {children}
          </CodePre>
        ),
        table: (props) => <MarkdownTable {...props} loading={isLoading} />,
        code({ inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const language = match ? match[1] : null;

          // Check the view mode to decide whether to render the iframe or the code block
          // Only use the iframe if the browser supports sandbox on iframe and viewCode is false
          if (
            !inline &&
            language === 'html' &&
            !viewCode &&
            'sandbox' in document.createElement('iframe')
          ) {
            const blob = new Blob([String(children)], { type: 'text/html' });
            const src = URL.createObjectURL(blob);

            return (
              //DO NOT allow-same-origin for sandbox
              <iframe
                {...props}
                src={src}
                style={{ width: '100%', height: '500px' }}
                sandbox="allow-scripts"
              />
            );
          } else if (!inline && match) {
            return (
              <StyledSyntaxHighlighter {...props} language={match[1]} style={a11yDark} PreTag="div">
                {String(children).replace(/\n$/, '')}
              </StyledSyntaxHighlighter>
            );
          } else {
            return (
              <code {...props} style={{ wordWrap: 'normal' }} className={className}>
                {children}
              </code>
            );
          }
        },
      }}
    >
      {renderMessage(message)}
    </ReactMarkdown>
  );
};

export default ChatReactMarkdown;
